// ─────────────────────────────────────────────────────────────────────────────
// Archivo: MxMain.js
// Descripción: Layout principal de la aplicación. (Variables CSS y Linter resuelto)
// ─────────────────────────────────────────────────────────────────────────────
import { MdOutlineDisplaySettings } from "react-icons/md";
import { CgOptions } from "react-icons/cg";
import { AiOutlineDesktop, AiOutlineReload, AiOutlineWarning } from "react-icons/ai";
import { BsGeoAlt, BsFillSendFill, BsCashCoin, BsFillCalendarFill, BsFillGearFill, BsFillCollectionFill } from "react-icons/bs";
import { FaRegClock, FaSquareFull, FaUserTie, FaUser, FaShoppingBasket, FaCoins, FaCashRegister, FaBox, FaBoxes, FaTruck, FaToolbox, FaFolderOpen, FaHistory, FaChair, FaMicroscope, FaShieldAlt } from "react-icons/fa";
import { IoLogoOctocat } from "react-icons/io";
import { BiImage, BiHelpCircle, BiExit } from "react-icons/bi";
import { MdHearing, MdCenterFocusStrong } from "react-icons/md";
import { GiMicroscopeLens, GiDogHouse, GiCancel } from "react-icons/gi";
import { TbReplace, TbHemisphere, TbCut, TbPrescription, TbStethoscope, TbVaccine, TbVectorTriangle } from "react-icons/tb";
import { LuGlasses } from "react-icons/lu";
import { RxActivityLog } from "react-icons/rx";
import { HiArrowsExpand, HiOutlineStatusOffline } from "react-icons/hi";
import { IoFileTrayFull } from "react-icons/io5";
import { ImStatsBars } from "react-icons/im";
import { IoMdAnalytics, IoMdOptions } from "react-icons/io";
import { FiUser, FiUsers } from "react-icons/fi";
// React y librerías base
import React from 'react';
import { Outlet, useNavigate } from "react-router-dom";
// Hooks personalizados del sistema
import { useTranslation } from '../hx/useTranslation';
import { useNav } from '../hx/useNav';
import { useSessionStorage } from '../hx/useSessionStorage';
import { useAccess } from '../hx/useAccess';
import { useWindowSize } from '../hx/useWindowSize';
import { useTabs } from '../hx/useTabs';
import useStableOnlineStatus from '../hx/useStableOnlineStatus';
import useMicroOutageLogger from '../hx/useMicroOutageLogger';
// Componentes de Material-UI
import AppBar from '@material-ui/core/AppBar';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import MenuIcon from '@material-ui/icons/Menu';
// Módulos y funciones utilitarias del sistema (Fx*)
import * as FxLog from '../fx/FxLog';
import * as FxStr from '../fx/FxStr';
import * as FxDat from '../fx/FxDat';
import * as FxFetch from '../fx/FxFetch';
import * as FxEdition from '../fx/FxEdition';
import * as FxInet from '../fx/FxInet';
import * as FxFile from '../fx/FxFile';
import * as FxGeolocation from '../fx/FxGeolocation';
import * as FxBrowser from '../fx/FxBrowser';
import * as FxRegion from '../fx/FxRegion';
import * as FxLicenseAgreement from '../fx/FxLicenseAgreement';
// Componentes propios y estado global (GxStore)
import TxTimerComms from '../modules/common/TxTimerComms';
import TxTimerLogins from '../modules/common/TxTimerLogins';
import { MxMainList, MxMainListItem, MxMainListGroup } from './MxMainList';
import {
  StoreClient, StoreHistory, StoreCalendarView, StoreWaiting, StoreCash, StoreMassBilling, StoreMarketing, StoreImage, StoreMultimedia,
  clearStore, getStoreValue, setStoreValue, isApp, getStoreValueBo
} from "../gx/GxStore";
// Componentes UI reutilizables (Cx*)
import CxDialog from '../cx/CxDialog';
import CxDlgTable from '../cx/CxDlgTable';
import CxDlgCalendar from '../cx/CxDlgCalendar';
import CxTooltip from '../cx/CxTooltip';
import CxState from '../cx/CxState';
import CxSnackbar from '../cx/CxSnackbar';
import CxDlgAlert from '../cx/CxDlgAlert';
import CxIconButtonNew from '../cx/CxIconButtonNew';
import CxIconButtonMenu from '../cx/CxIconButtonMenu';
import CxLink from '../cx/CxLink';
import CxDlgUpgrade from '../cx/CxDlgUpgrade';
import TricolorSVG from '../svg/TricolorSVG.jsx';
// Componente de Pestañas Extraído + Hook de gestión
import MxMainTabs from './MxMainTabs';

const getAppbarBackground = () => {
  let hex = FxStr.rgb2hex(`rgb(${(getStoreValue('StoreSession', 'txBranchRgb') || '255-255-255')})`);
  return hex;
};

// ─── ESTILOS (Material-UI) ────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  root: { display: 'flex' },
  drawer: {
    [theme.breakpoints.up('sm')]: {
      width: props => props.boSidebarCollapsed ? 'var(--main-drawer-width-collapsed)' : 'var(--main-drawer-width)',
      flexShrink: 0,
      transition: 'width 0.3s ease',
    },
  },
  appBar: {
    padding: '0 0 0 8px', 
    height: 'var(--main-appbar-height)',
    background: getAppbarBackground(),
    color: 'var(--ibs_color_black)',
    "&.MuiPaper-elevation4": {
      boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 0px 0px, rgba(0, 0, 0, 0.06) 0px 1px 0px 0px',
    },
    [theme.breakpoints.up('sm')]: {
      width: props => props.boSidebarCollapsed ? 'calc(100% - var(--main-drawer-width-collapsed))' : 'calc(100% - var(--main-drawer-width))',
      marginLeft: props => props.boSidebarCollapsed ? 'var(--main-drawer-width-collapsed)' : 'var(--main-drawer-width)',
      transition: 'width 0.3s ease, margin-left 0.3s ease',
    },
  },
  menuButton: {
    padding: '5px', 
    marginRight: '4px', 
    [theme.breakpoints.up('sm')]: { display: 'none' },
  },
  appbarTitle: {
    width: '100%',
    paddingLeft: '0px',
    paddingBottom: '5px',
    textAlign: 'left',
    color: theme.palette.getContrastText(getAppbarBackground()),
    fontFamily: 'var(--ibs_brand_font_family)',
    fontSize: '11px',
    lineHeight: '14px',
    [theme.breakpoints.up('sm')]: { fontSize: '20px', paddingLeft: '0px', lineHeight: '20px' },
  },
  appbarWarning: { color: 'red', paddingTop: '10px', paddingLeft: '6px' },
  appbarButton: { color: theme.palette.getContrastText(getAppbarBackground()) },
  appbarMenu: { color: theme.palette.getContrastText(getAppbarBackground()) },
  appbarText: {
    lineHeight: 1.2,
    color: theme.palette.getContrastText(getAppbarBackground()),
    textAlign: 'center',
    textTransform: 'uppercase',
    fontFamily: 'var(--ibs_control_label_font_family)',
    fontSize: '9px',
    display: 'inline',
    cursor: 'pointer',
    '&:hover': { backgroundColor: 'yellow' },
  },
  appbarActions: { paddingLeft: '6px', paddingBottom: '6px', display: 'flex' },
  appbarDivider: { height: '50px', [theme.breakpoints.up('sm')]: { height: '42px' } },
  toolbar: { padding: '0px' },
  drawerPaper: { 
    width: props => props.boSidebarCollapsed ? 'var(--main-drawer-width-collapsed)' : 'var(--main-drawer-width)',
    transition: 'width 0.3s ease',
    overflowX: 'hidden'
  },
  drawerContainer: {
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.15)',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(0, 0, 0, 0.30)',
    },
  },
  content: {
    marginTop: 'var(--main-appbar-height)',
    background: 'var(--ibs_color_white)',
    flexGrow: 1,
    padding: theme.spacing(0),
    [theme.breakpoints.up('sm')]: {
      marginTop: props => props.showTabs ? 'calc(var(--main-appbar-height) + var(--main-tab-bar-height))' : 'var(--main-appbar-height)',
    },
  },
}));

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const MxMain = (props) => {
  // Inicialización de Hooks
  const [{ navTo, navClear }] = useNav();
  const [{ trans }] = useTranslation();
  const [{ access, isMatchingLang }] = useAccess();
  
  // Estado para el menú colapsable
  const [boSidebarCollapsed, setBoSidebarCollapsed] = React.useState(() => {
    return localStorage.getItem('MxMain.boSidebarCollapsed') === 'Y';
  });  
  const toggleSidebar = () => {
    setBoSidebarCollapsed(prev => {
      const newState = !prev;
      localStorage.setItem('MxMain.boSidebarCollapsed', newState ? 'Y' : 'N');
      return newState;
    });
  };

  const size = useWindowSize();
  const showTabs = size.isLarge && getStoreValueBo('StoreSession', 'boShowTabs');
  
  // Pasamos showTabs a los estilos para que el marginTop calcule bien
  const classes = useStyles({ boSidebarCollapsed, showTabs });
  const theme = useTheme();
  const navigate = useNavigate();
  
  const [useReceipts,] = React.useState(getStoreValueBo('StoreSession', 'boUseReceipts'));
  const isOnline = useStableOnlineStatus(2500);

  // Creado por Asistente de Programación el 2026-07-19
  // Hook independiente de useStableOnlineStatus: detecta y loguea micro-cortes
  // reales de internet (1-2s) haciendo ping a servicios externos estables.
  // No afecta al icono/snackbar, que sigue gestionado por useStableOnlineStatus.
  useMicroOutageLogger({
    intervalMs: 3000,
    timeoutMs: 1500,
    onFlush: async (strLogs) => {
      const arResult = await Promise.all([FxLog.logErrorToDb('MxMain', 'useMicroOutageLogger', `Internet Outage: ${strLogs}`)]);
      return arResult[0] && (arResult[0].theValue === 'OK' || arResult[0].status === 'OK');
    },
  });

  // Referencias a elementos UI
  const r_dlgtable_branches = React.useRef(null);
  const r_boLicenseAgreementAccepted = React.useRef(null);
  const r_dlg_alert = React.useRef(null);
  const r_dlg_upgrade = React.useRef(null);
  const r_snack_internet_off = React.useRef(null);
  const r_snack_internet_on = React.useRef(null);

  // Estados locales
  const [dlgAccessDenied, setDlgAccessDenied] = React.useState(false);
  const [dlgChangeBranch, setDlgChangeBranch] = React.useState(false);
  const [dlgChangeDate, setDlgChangeDate] = React.useState(false);
  const [currentDate, setCurrentDate] = React.useState(`${FxDat.toLocalDate(getStoreValue('StoreSession', 'dtLogin').substring(0, 10))}`);
  const [currentDateColor, setCurrentDateColor] = React.useState(getStoreValue('StoreSession', 'dtLogin').substring(0, 10) === FxDat.todayReal() ? 'black' : 'red');

  // Estados de los Stores
  const [storeClient,] = useSessionStorage('StoreClient', StoreClient);
  const [storeMassBilling,] = useSessionStorage('StoreMassBilling', StoreMassBilling);
  const [storeMarketing,] = useSessionStorage('StoreMarketing', StoreMarketing);
  const [storeCash,] = useSessionStorage('StoreCash', StoreCash);
  const [storeHistory,] = useSessionStorage('StoreHistory', StoreHistory);
  const [storeCalendarView, setStoreCalendarView] = useSessionStorage('StoreCalendarView', StoreCalendarView);
  const [storeWaiting,] = useSessionStorage('StoreWaiting', StoreWaiting);
  const [storeImage,] = useSessionStorage('StoreImage', StoreImage);
  const [storeMultimedia,] = useSessionStorage('StoreMultimedia', StoreMultimedia);
  const [isMasterUser,] = React.useState(getStoreValue('StoreSession', 'txUser') === 'master');
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [groupFiles, setGroupFiles] = React.useState(true);
  const [groupActivity, setGroupActivity] = React.useState(true);
  const [groupSales, setGroupSales] = React.useState(true);
  const [groupUtilities, setGroupUtilities] = React.useState(true);
  const [groupSettings, setGroupSettings] = React.useState(true);
  const [dlgLicenseAgreement, setDlgLicenseAgreement] = React.useState(false);

  const isAccordionStorageKey = (strKey) =>
    (strKey.startsWith('MxAnimalForm.') || strKey.startsWith('MxClientForm.'))
    && strKey.includes('.accordion.');

  // ─── GESTIÓN DE PESTAÑAS ──────────────────────────────────────────────────
  const {
    tabs,
    activeTabId,
    gotoBlank,
    openTab,
    openTabWithNavigation,
    handleReorderTabs,
    switchToTab,
    closeTab,
    closeAllTabs,
    duplicateTab,
    togglePinTab,
  } = useTabs({ navigate, navClear, navTo, isStartupResetKey: isAccordionStorageKey });

  // ─── EFECTOS DE INICIALIZACIÓN ────────────────────────────────────────────
  React.useEffect(() => {
    const handleContextMenu = (obEvent) => {
      const strTagName = obEvent.target.tagName;
      const boIsTextElement = strTagName === 'INPUT' || strTagName === 'TEXTAREA';
      
      if (!boIsTextElement) {
        obEvent.preventDefault();
      }
    };
    
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!isMasterUser) {
      Promise.all([FxLicenseAgreement.isAccepted()]).then((result) => {
        FxLog.infox('MxMain.useEffect', '...:', result[0]);
        if (!result[0]) setDlgLicenseAgreement(true);
      });
    }
    if (!isMatchingLang()) {
      r_dlg_alert.current.setText(trans('msg.need_refresh_accesses'));
      r_dlg_alert.current.setTextColor('var(--ibs_color_red)');
      r_dlg_alert.current.setHasButtonClose(true);
      r_dlg_alert.current.setOpen(true);
    }

    const existingTabs = JSON.parse(sessionStorage.getItem('MxMain.tabs') || '[]');
    if (existingTabs.length === 0) {
      const startup_mod = localStorage.getItem('MxLogin.startup_mod') || 'client-list';
      const ar = JSON.parse(sessionStorage.getItem('access') || '{}');
      const startupLabel = FxStr.choose(startup_mod, 'client-list|sls-doc-list|calendar-view',
        trans('field.clients'), trans('field.pos'), trans('field.calendar')
      ) || trans('field.clients');

      const hasAccess = ar[startupLabel] !== 'N';
      const url = hasAccess ? startup_mod : 'blank';
      const label = hasAccess ? startupLabel : 'blank';
      const accessKey = hasAccess ? startupLabel : 'blank';

      FxLog.infox('MxMain.useEffect', '...url:', url);
      if (url === 'blank') {
        gotoBlank();
      } else {
        goto(accessKey, url, label);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Snackbars de aviso visual de online/offline (con el debounce de useStableOnlineStatus).
  // El logueo de cortes ahora corre aparte en useMicroOutageLogger, arriba.
  const lastOnlineRef = React.useRef(null);
  React.useEffect(() => {
    if ((localStorage.getItem('MxMain.inet_outage_notice') || 'Y') === 'N') return;
    if (lastOnlineRef.current === null) { lastOnlineRef.current = isOnline; return; }
    if (isOnline !== lastOnlineRef.current) {
      lastOnlineRef.current = isOnline;
      if (isOnline) {
        if (r_snack_internet_on.current) r_snack_internet_on.current.setOpen(true);
      } else {
        if (r_snack_internet_off.current) r_snack_internet_off.current.setOpen(true);
      }
    }
  }, [isOnline]);

  // ─── EFECTO: exponer openTabWithNavigation para componentes hijo ──────────
  React.useEffect(() => {
    window.__mxMain_openTabWithNavigation = (strIconKey, strLabel, boOptions = true, strTabInstanceKey = null) =>
      openTabWithNavigation(strIconKey, strLabel, showTabs, boOptions, strTabInstanceKey);

    return () => {
      delete window.__mxMain_openTabWithNavigation;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTabs]);

  const goto = (txSecAccess, url, label) => {
    FxLog.infox('MxMain.goto', '...access(txSecAccess):', txSecAccess + ':' + access(txSecAccess));
    if (access(txSecAccess) === 'N') {
      setDlgAccessDenied(true);
      return;
    }

    setMobileOpen(false);
    const isDesktopNow = window.innerWidth >= 600;
    const boShowTabs = getStoreValueBo('StoreSession', 'boShowTabs');

    if (isDesktopNow) {
      const alreadyHandled = openTab(url, label, boShowTabs);
      if (alreadyHandled) return;
    }

    clearStore('StoreClient', storeClient);
    clearStore('StoreHistory', storeHistory);
    clearStore('StoreWaiting', storeWaiting);
    clearStore('StoreCash', storeCash);
    clearStore('StoreMassBilling', storeMassBilling);
    clearStore('StoreMarketing', storeMarketing);
    clearStore('StoreImage', storeImage);
    clearStore('StoreMultimedia', storeMultimedia);
    
    clearStore('StoreCalendarView', storeCalendarView);
    storeCalendarView.selectedView = localStorage.getItem('MxCalendarView.lastCalendarView')||'';
    storeCalendarView.selectedDate = localStorage.getItem('MxCalendarView.lastCalendarDate')||'';
    setStoreCalendarView(storeCalendarView);

    navClear();
    Promise.all([navTo('blank')]).then(() => navTo(url));
  };

  const getItem = (icon, moduleKey, moduleName, accessName, url) => {
    let iconColor = '';
    switch (access(accessName)) {
      case 'N': iconColor = 'var(--ibs_color_red)'; break;
      case 'R': iconColor = 'var(--ibs_color_yellow)'; break;
      default: iconColor = 'var(--ibs_color_black)'; break;
    }
    const isInEdition = FxEdition.isInEdition(moduleKey);
    return (
      <MxMainListItem
        icon={icon}
        iconColor={iconColor}
        text={moduleName}
        textColor={isInEdition ? 'var(--ibs_color_black)' : 'var(--ibs_color_gray)'}
        type='nested'
        onClick={() => goto(accessName, isInEdition ? url : 'upgrade', moduleName)}
      />
    );
  };

  // ─── HANDLERS DE ACCIONES DE MENÚ SUPERIOR ────────────────────────────────
  const doHelp = (option) => {
    if (option === 'manual') FxBrowser.windowOpen(`https://iberical.com/ibericalhelp/${getStoreValue('StoreSession', 'txLocale').substring(0, 2)}_${getStoreValue('StoreSession', 'txApp')}.php`);
    if (option === 'license') FxBrowser.windowOpen(`/html/license_agreement_${getStoreValue('StoreSession', 'txLocale').substring(0, 2)}.htm`);
    if (option === 'verifactu') FxBrowser.windowOpen(`https://iberical.com/ibericalhelp//Declaracion_Responsable_Verifactu.pdf`);
  };

  const doExit = () => {
    let record = {};
    record['process'] = 'silent_login_set_exit';
    Promise.all([FxFetch.doJsonX('login/set_exit', record)]).then((result) => {
      if (result[0].theValue === 'OK') {
        const path = getStoreValue('StoreSession', 'inClientCP') ? 'cp' : 'login';
        sessionStorage.clear();
        navigate(`/${path}`, { replace: true });
      }
    });
  };

  const doOptions = (option) => {
    if (option === 'clocking') {
      if (FxEdition.getEdition() !== 'PM' && FxEdition.getEdition() !== 'PF') {
        r_dlg_upgrade.current.setOpen(true);
      } else {
        navTo('clocking');
      }
    }
    if (option === 'reload') {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          for (let reg of registrations) reg.unregister();
        });
      }
      if ('caches' in window) {
        caches.keys().then((names) => { for (let name of names) caches.delete(name); });
      }
      window.location.reload(true);
    }
    if (option === 'geolocation') {
      Promise.all([FxGeolocation.getGeolocation()]).then((result) => {
        FxLog.infox('MxMain.doOptions', '...:', result[0]);
        if (result[0].status === 'OK') {
          r_dlg_alert.current.setText(result[0].city + '\n' + result[0].latitude + ' ' + result[0].longitude);
          r_dlg_alert.current.setTextColor('var(--ibs_color_black)');
        } else {
          r_dlg_alert.current.setText(result[0].status);
          r_dlg_alert.current.setTextColor('var(--ibs_color_red)');
        }
        r_dlg_alert.current.setHasButtonClose(true);
        r_dlg_alert.current.setOpen(true);
      });
    }
    if (option === 'rustdesk') FxFile.downloadFile('https://www.iberical.com/downloads/IBERICAL-RD.exe');
  };

  const doChangeBranch = (row) => {
    try {
      setDlgChangeBranch(false);
      if (row) {
        localStorage.setItem('MxLogin.branch', `${getStoreValue('StoreSession', 'txIdi')}|${row.original.inBranch}`);
        const path = getStoreValue('StoreSession', 'inClientCP') ? 'cp' : 'login';
        sessionStorage.clear();
        navigate(`/${path}`, { replace: true });
      }
    } catch (error) {
      FxLog.errorx("MxMain.doChangeBranch", error);
    }
  };

  const doChangeDate = (d) => {
    try {
      const isoDate = d || '';
      setDlgChangeDate(false);
      if (isoDate) {
        FxLog.infox('MxMain.doChangeDate', '...isoDate:', isoDate);
        setStoreValue('StoreSession', 'dtLogin', isoDate);
        setCurrentDate(FxDat.toLocalDate(isoDate));
        setCurrentDateColor(isoDate === FxDat.todayReal() ? 'black' : 'red');
      }
    } catch (error) {
      FxLog.errorx("MxMain.doChangeDate", error);
    }
  };

  const getCompanyName = () => {
    let rv = getStoreValue('StoreSession', 'txCompany').toUpperCase();
    if (Boolean(getStoreValue('StoreSession', 'txIdi').includes('demo'))) rv += " DEMO";
    return rv;
  };

  // ─── RENDER DE LA BARRA LATERAL ───────────────────────────────────────────
  const drawer = (
    <div className={classes.drawerContainer}>
      <div className={classes.toolbar} />
      <MxMainList 
        boSidebarCollapsed={boSidebarCollapsed} 
        fxToggleSidebar={toggleSidebar} 
        isLarge={size.isLarge}
        fxToggleMobile={() => setMobileOpen(false)}
      >
        <MxMainListGroup icon={<FaFolderOpen />} text={trans('field.files')} group={groupFiles} onClick={() => setGroupFiles(!groupFiles)}>
          {getItem(<FaUser fontSize={16} style={{ margin: '0 0 0 0px' }} />, 'CLI', trans('field.clients'), trans('field.clients'), 'client-list')}
          {getItem(<FaUserTie fontSize={16} style={{ margin: '0 0 0 0px' }} />, 'EMP', trans('field.employees'), trans('field.employees'), 'employee-list')}
          {getItem(<FaTruck />, 'SUP', trans('field.suppliers'), trans('field.suppliers'), 'supplier-list')}
          {isApp('opti') && getItem(<FaMicroscope />, 'LAB', trans('field.labs'), trans('field.labs'), 'lab-list')}
          {getItem(<FaBox fontSize={15} />, 'PSS', trans('field.pss'), trans('field.pss'), 'ps-list')}
          {getItem(<TbVectorTriangle fontSize={20} style={{ margin: '0 0 0 -2px' }} />, 'REF', trans('field.referrers'), trans('field.referrers'), 'referrer-list')}
        </MxMainListGroup>

        {(isApp('opti') || isApp('vett') || isApp('spaw')) &&
          <MxMainListGroup icon={<RxActivityLog />} text={trans('field.activity')} group={groupActivity} onClick={() => setGroupActivity(!groupActivity)}>
            {isApp('opti') && getItem(<LuGlasses fontSize={19} style={{ margin: '0 0 0 -2px' }} />, 'CLI', trans('field.rx_glas'), trans('field.rx_glas'), 'activity-opti-g-list')}
            {isApp('opti') && getItem(<TbHemisphere fontSize={19} style={{ margin: '0 0 0 -2px' }} />, 'CLI', trans('field.rx_clen'), trans('field.rx_clen'), 'activity-opti-c-list')}
            {isApp('opti') && getItem(<MdHearing fontSize={19} style={{ margin: '0 0 0 -2px' }} />, 'CLI', trans('field.rx_haid'), trans('field.rx_haid'), 'activity-opti-h-list')}
            {isApp('opti') && getItem(<TbReplace fontSize={19} style={{ margin: '0 0 0 -2px' }} />, 'CLI', trans('field.repls'), trans('field.repls'), 'activity-opti-r-list')}
            {isApp('opti') && getItem(<GiMicroscopeLens fontSize={19} style={{ margin: '0 0 0 -2px' }} />, 'CLI', trans('field.lab_orders'), trans('field.lab_orders'), 'activity-opti-l-list')}
            {isApp('vett') && getItem(<TbVaccine fontSize={19} style={{ margin: '0 0 0 -2px' }} />, 'CLI', trans('field.vaccs'), trans('field.vaccs'), 'activity-vett-v-list')}
            {isApp('vett') && getItem(<TbStethoscope fontSize={19} style={{ margin: '0 0 0 -2px' }} />, 'CLI', trans('field.cnss'), trans('field.cnss'), 'activity-vett-c-list')}
            {isApp('vett') && getItem(<TbPrescription fontSize={18} style={{ margin: '0 0 0 -2px' }} />, 'CLI', trans('field.rxs'), trans('field.rxs'), 'activity-vett-r-list')}
            {(isApp('vett') || isApp('spaw')) && getItem(<TbCut fontSize={19} style={{ margin: '0 0 0 -2px' }} />, 'CLI', trans('field.grooming'), trans('field.grooming'), 'activity-vett-g-list')}
            {isApp('vett') && getItem(<GiDogHouse fontSize={17} style={{ margin: '0 0 0 -2px' }} />, 'CLI', trans('field.boarding'), trans('field.boarding'), 'activity-vett-b-list')}
          </MxMainListGroup>
        }

        <MxMainListGroup icon={<BsCashCoin />} text={trans('field.financial')} group={groupSales} onClick={() => setGroupSales(!groupSales)}>
          {getItem(<FaCoins />, 'SLS', trans('field.sales'), trans('field.pos'), 'sls-doc-list')}
          {getItem(<FaShoppingBasket />, 'PUR', trans('field.purchases'), trans('field.purchases'), 'pur-doc-list')}
          {getItem(<FaHistory />, 'HIS', trans('field.history'), trans('field.pos'), 'history-filter')}
          {getItem(<FaCashRegister />, 'CSH', trans('field.cash_reg'), trans('field.cash_reg'), 'cash-filter')}
          {getItem(<FaBoxes />, 'STO', trans('field.stockrooms'), trans('field.stockrooms'), 'stock-list')}
          {getItem(<ImStatsBars />, 'STA_bas', trans('field.stats'), trans('field.stats'), 'stats')}
          {getItem(<IoMdAnalytics />, 'ANL', trans('field.analytics'), trans('field.analytics'), 'analytics-list')}
          {useReceipts && getItem(<BsFillCollectionFill />, 'BLG', trans('field.mass_billing'), trans('field.mass_billing'), 'mass-billing-filter')}
        </MxMainListGroup>

        <MxMainListGroup icon={<FaToolbox />} text={trans('field.utilities')} group={groupUtilities} onClick={() => setGroupUtilities(!groupUtilities)}>
          {getItem(<IoLogoOctocat fontSize={20} style={{ margin: '0 0 0 -2px' }} />, 'AIS', 'Iberix AI', trans('field.ai'), 'ai-form')}
          {!isApp('invo') && getItem(<BiImage fontSize={21} style={{ margin: '0 0 0 -2px' }} />, 'IMG', trans('field.images'), trans('field.images'), 'image-list')}
          {getItem(<IoFileTrayFull fontSize={19} style={{ margin: '0 0 0 -2px' }} />, 'MME', trans('field.multimedia_files'), trans('field.multimedia'), 'multimedia-list')}
          {getItem(<BsFillCalendarFill fontSize={15} />, 'CAL', trans('field.calendar'), trans('field.calendar'), 'calendar-view')}
          {getItem(<BsFillSendFill fontSize={17} />, 'COM', trans('field.comms'), trans('field.comms'), 'comm-list')}
          {getItem(<HiArrowsExpand fontSize={20} style={{ margin: '0 0 0 -2px' }} />, 'MKT', trans('field.marketing'), trans('field.marketing'), 'marketing-filter')}
          {(isApp('vett') || isApp('spaw')) && getItem(<FaChair />, 'WAI', trans('field.waiting_room'), trans('field.waiting_room'), 'waiting-list')}
          {getItem(<FaShieldAlt />, 'PLN', trans('field.plans'), trans('field.plans'), 'plan-list')}
        </MxMainListGroup>

        <MxMainListGroup icon={<BsFillGearFill />} text={trans('field.settings')} group={groupSettings} onClick={() => setGroupSettings(!groupSettings)}>
          {getItem(<MdCenterFocusStrong fontSize={20} />, 'BCH', trans('field.branches'), trans('field.branches'), 'sett-branch-list')}
          {getItem(<IoMdOptions />, 'OPT', trans('field.options'), trans('field.options'), 'options')}
          {getItem(<FiUser />, 'USR', trans('field.users'), trans('field.security'), 'sett-sec-user-list')}
          {getItem(<FiUsers />, 'GRP', trans('field.groups'), trans('field.security'), 'sett-sec-group-list')}
          {getItem(<CgOptions />, 'PFU', `${trans('field.prefs')}-${trans('field.user')}`, `${trans('field.prefs')}-${trans('field.user')}`, 'prefs-user-form')}
          {getItem(<MdOutlineDisplaySettings />, 'PFL', `${trans('field.prefs')}-${trans('field.locals')}`, `${trans('field.prefs')}-${trans('field.locals')}`, 'prefs-locals-form')}
        </MxMainListGroup>
      </MxMainList>
      <Divider />
    </div>
  );

  // ─── RENDER PRINCIPAL ─────────────────────────────────────────────────────
  return (
    <div className={classes.root}>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          {/* Se elimina edge="start" para que respete el padding izquierdo de la barra */}
          <IconButton className={classes.menuButton} color="inherit" onClick={() => setMobileOpen(!mobileOpen)}>
            <MenuIcon />
          </IconButton>

          <Typography className={classes.appbarTitle}>
            {getCompanyName() + ((FxInet.isDebug() && !getCompanyName().includes('DEMO')) ? ' (DEBUG)' : '')}
          </Typography>

          <div className={classes.appbarActions}>
            <Divider className={classes.appbarDivider} orientation="vertical" light={true} flexItem />

            {FxEdition.isPremiumNeeded() &&
              <CxTooltip text={trans('msg.app_server_resources_required')} arrow>
                <Icon className={classes.appbarWarning}><AiOutlineWarning /></Icon>
              </CxTooltip>
            }

            {!isOnline &&
              <CxTooltip text={trans('msg.app_internet_off')} arrow>
                <Icon className={classes.appbarWarning}><HiOutlineStatusOffline /></Icon>
              </CxTooltip>
            }

            <CxIconButtonMenu onSelection={doOptions} className={classes.appbarMenu} icon={<TricolorSVG size={24} />}
              options={[
                { id: 'clocking', text: trans('field.clocking'), icon: <FaRegClock /> },
                { id: 'reload', text: trans('field.reload'), icon: <AiOutlineReload /> },
                { id: 'geolocation', text: trans('field.geolocation'), icon: <BsGeoAlt /> },
                { id: 'rustdesk', text: trans('field.download_x', 'Rustdesk'), icon: <AiOutlineDesktop /> },
              ]}
            />

            {size.isLarge &&
              <>
                <Divider className={classes.appbarDivider} orientation="vertical" light={true} flexItem />
                <CxIconButtonMenu onSelection={doHelp} className={classes.appbarMenu} icon={<BiHelpCircle />}
                  options={FxRegion.isSpain()
                    ? [
                      { id: 'manual', text: trans('field.manual_user') },
                      { id: 'license', text: trans('field.license_agreement') },
                      { id: 'verifactu', text: 'Declaración Responsable Veri-Factu' },
                    ]
                    : [
                      { id: 'manual', text: trans('field.manual_user') },
                      { id: 'license', text: trans('field.license_agreement') },
                    ]
                  }
                />
              </>
            }

            <Divider className={classes.appbarDivider} orientation="vertical" light={true} flexItem />

            <CxTooltip text={trans('field.exit')} arrow>
              <IconButton className={classes.appbarButton} onClick={doExit}><BiExit /></IconButton>
            </CxTooltip>

            <Divider className={classes.appbarDivider} orientation="vertical" light={true} flexItem />

            <div style={{ display: 'flex', flexDirection: 'column', margin: '10px 4px 0 4px', gap: '3px' }}>
              <CxTooltip text={`${trans('field.user')} / ${trans('field.branch_id')}`} arrow>
                <Typography noWrap className={classes.appbarText}
                  style={{ borderTop: '1px dotted var(--ibs_color_gray)' }}
                  onClick={() => {
                    if (FxEdition.getEdition() !== 'FR') {
                      if (getStoreValue('StoreSession', 'inBranchForced')) {
                        r_dlg_alert.current.setText(trans('msg.user_not_allowed_change_branches'));
                        r_dlg_alert.current.setTextColor('var(--ibs_color_red)');
                        r_dlg_alert.current.setHasButtonClose(true);
                        r_dlg_alert.current.setOpen(true);
                      } else {
                        setDlgChangeBranch(true);
                      }
                    }
                  }}
                >
                  {`${getStoreValue('StoreSession', 'txUser')}-${getStoreValue('StoreSession', 'txBranchId')}`}
                </Typography>
              </CxTooltip>

              <CxTooltip text={`${trans('field.work_date')}`} arrow>
                <Typography noWrap className={classes.appbarText}
                  style={{ borderBottom: '1px dotted var(--ibs_color_gray)', color: currentDateColor }}
                  onClick={() => { if (FxEdition.getEdition() !== 'FR') setDlgChangeDate(true); }}
                >
                  {currentDate}
                </Typography>
              </CxTooltip>
            </div>
          </div>
        </Toolbar>
      </AppBar>

      {/* Componente de Pestañas Extraído */}
      <MxMainTabs
        tabs={tabs}
        activeTabId={activeTabId}
        onSwitchTab={switchToTab}
        onCloseTab={closeTab}
        onDuplicateTab={duplicateTab}
        onCloseAllTabs={closeAllTabs}
        onReorderTabs={handleReorderTabs}
        showTabs={showTabs}
        onTogglePinTab={togglePinTab}
        trans={trans}
        boSidebarCollapsed={boSidebarCollapsed}
      />

      <nav className={classes.drawer}>
        <Hidden smUp implementation="css">
          <Drawer
            classes={{ paper: classes.drawerPaper }}
            variant="temporary"
            anchor={theme.direction === 'rtl' ? 'right' : 'left'}
            open={mobileOpen}
            onClose={() => setMobileOpen(!mobileOpen)}
            ModalProps={{ keepMounted: true }}
          >
            {drawer}
          </Drawer>
        </Hidden>
        <Hidden xsDown implementation="css">
          <Drawer classes={{ paper: classes.drawerPaper }} variant="permanent" open>
            {drawer}
          </Drawer>
        </Hidden>
      </nav>

      <main className={classes.content}>
        <TxTimerComms />
        <TxTimerLogins />

        <Outlet key={activeTabId} context={{ boSidebarCollapsed }} />

        <CxDialog title={trans('field.license_agreement')} open={dlgLicenseAgreement} onClose={() => doExit()}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left', gap: 10 }}>
            <CxLink
              label={`[${trans('msg.see_x', 'field.license_agreement').toUpperCase()}]`}
              style={{ margin: '0 0 0 0px', fontSize: '10px' }}
              onClick={() => doHelp('license')}
            />
            <CxState
              mode='checkbox'
              xref={r_boLicenseAgreementAccepted}
              label={trans('field.i_accept_the_agreement')}
              width="250px"
              onClick={() => { setDlgLicenseAgreement(false); FxLicenseAgreement.saveAccepted(); }}
            />
          </div>
        </CxDialog>

        <CxDialog title={trans('field.security')} open={dlgAccessDenied} onOk={() => setDlgAccessDenied(false)} height='200px'>
          <div style={{ width: '200px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <GiCancel fontSize="30px" style={{ color: 'var(--ibs_color_red)' }} />
            <span>{trans('msg.access_denied')}</span>
          </div>
        </CxDialog>

        <CxDlgTable
          xref={r_dlgtable_branches}
          open={dlgChangeBranch}
          onSelect={doChangeBranch}
          onClose={() => setDlgChangeBranch(false)}
          url={'sett/branches'}
          columns={[
            { id: "0", Header: "ID", accessor: "inBranch", show: false },
            { id: "1", Header: trans('field.branch'), accessor: "txBranch", width: '50%' },
            { id: "2", Header: trans('field.branch_id'), accessor: "txBranchId", width: '25%' },
            {
              id: "3", Header: trans('field.color'),
              accessor: row => <CxIconButtonNew icon={<FaSquareFull fontSize="20px" style={{ color: FxStr.rgb2hex(row["txBranchRgb"]), border: '1px solid gray' }} />} />,
              width: '25%', align: 'center'
            },
          ]}
          title={trans('field.branch_restart')}
        />

        <CxDlgCalendar
          open={dlgChangeDate}
          onClose={(d) => doChangeDate(d)}
          title={trans('field.change_x', 'field.work_date')}
        />

        <CxSnackbar xref={r_snack_internet_off} severity="error" value={trans('msg.app_internet_off')} />
        <CxSnackbar xref={r_snack_internet_on} severity="success" value={trans('msg.app_internet_on')} />

        <CxDlgAlert xref={r_dlg_alert} />
        <CxDlgUpgrade xref={r_dlg_upgrade} />
      </main>
    </div>
  );
};

export default MxMain;